/**
 * Visual Assistant Tools Registration
 *
 * Register all visual assistant tools with the ClientToolsRegistry.
 * This file should be imported in VoiceSDK._registerBuiltInTools()
 */

import { extractDOMContext } from './dom-context.js';
import {
  highlightElement,
  scrollTo,
  navigateTo,
  fillForm,
  clickElement,
  clearHighlights
} from './dom-actions.js';
import { captureScreenshot } from './screenshot.js';

/**
 * Register all visual assistant tools with the registry
 * All tools are always registered - backend controls what gets called
 *
 * @param {ClientToolsRegistry} registry - The tools registry
 */
export function registerVisualTools(registry) {
  // Register read_page tool (matches backend name)
  registry.registerHandler('read_page', async (params = {}) => {
    console.log('[TTP] 📋 Reading page content...');
    const context = await extractDOMContext(params);
    console.log('[TTP] ✅ Page content extracted:', {
      contentChars: context.metadata.totalContentChars,
      contentItems: context.metadata.contentItemCount,
      truncated: context.contentTruncated,
      source: context.contentSource,
      buttons: context.metadata.buttonsCount,
      links: context.metadata.linksCount,
      forms: context.metadata.formsCount,
    });
    return context;
  });

  // Register capture_screen tool — screenshot of the user's screen
  registry.registerHandler('capture_screen', async (params = {}) => {
    const {
      selector = null,
      format = 'jpeg',
      quality = 0.85,
      scale = 1,
      fullPage = false,
      maxWidth = 1280,
      maxHeight = 1280,
    } = params;

    console.log('[TTP] 📸 Capturing screen...', selector ? { selector } : '');
    const result = await captureScreenshot({
      selector,
      format,
      quality,
      scale,
      fullPage,
      maxWidth,
      maxHeight,
    });
    console.log(`[TTP] ✅ Screenshot captured: ${result.width}x${result.height}, ${Math.round(result.image.length / 1024)}KB`);
    return result;
  });

  // Register highlight tool
  registry.registerHandler('highlight_element', async (params = {}) => {
    console.log('[TTP] ✨ Highlighting element:', params.selector);
    const result = await highlightElement(params);
    if (result.success) {
      console.log('[TTP] ✅ Element highlighted');
    } else {
      console.warn('[TTP] ⚠️ Highlight failed:', result.error);
    }
    return result;
  });

  // Register scroll tool (matches backend name: scroll_to_element)
  registry.registerHandler('scroll_to_element', async (params = {}) => {
    // Log what type of scroll is being performed
    if (params.selector) {
      console.log('[TTP] 📜 Scrolling to element:', params.selector);
    } else if (params.direction) {
      console.log('[TTP] 📜 Scrolling', params.direction, 'by', params.amount || 500, 'px');
    } else if (params.x !== undefined || params.y !== undefined) {
      console.log('[TTP] 📜 Scrolling to position:', `(${params.x ?? 'current'}, ${params.y ?? 'current'})`);
    } else {
      console.log('[TTP] 📜 Scroll called with params:', params);
    }

    const result = await scrollTo(params);
    if (result.success) {
      console.log('[TTP] ✅ Scrolled successfully');
    } else {
      console.warn('[TTP] ⚠️ Scroll failed:', result.error);
    }
    return result;
  });

  // Register navigate tool
  registry.registerHandler('navigate_to', async (params = {}) => {
    console.log('[TTP] 🔗 Navigating to:', params.url);
    const result = await navigateTo(params);
    if (result.success) {
      console.log('[TTP] ✅ Navigation initiated');
    } else {
      console.warn('[TTP] ⚠️ Navigation blocked:', result.error);
    }
    return result;
  });

  // Register fill tool (matches backend name: fill_field)
  registry.registerHandler('fill_field', async (params = {}) => {
    console.log('[TTP] 📝 Filling field:', params.selector);
    const result = await fillForm(params);
    if (result.success) {
      console.log('[TTP] ✅ Field filled');
    } else {
      console.warn('[TTP] ⚠️ Fill failed:', result.error);
    }
    return result;
  });

  // Register click tool
  registry.registerHandler('click_element', async (params = {}) => {
    console.log('[TTP] 👆 Clicking element:', params.selector);
    const result = await clickElement(params);
    if (result.success) {
      console.log('[TTP] ✅ Element clicked');
    } else {
      console.warn('[TTP] ⚠️ Click failed:', result.error);
    }
    return result;
  });

  // Verify all tools were registered (matches backend tool names)
  const registeredTools = registry.getRegisteredTools();
  const expectedTools = ['read_page', 'capture_screen', 'highlight_element', 'scroll_to_element', 'navigate_to', 'fill_field', 'click_element'];
  const missingTools = expectedTools.filter(tool => !registeredTools.includes(tool));

  if (missingTools.length > 0) {
    console.error('[TTP] ❌ Some visual tools failed to register:', missingTools);
  }
}

/**
 * Extract page context for sending to backend
 * Called on call start and after navigation
 *
 * @returns {Promise<Object>} - Page context with DOM info
 */
export async function extractPageContext() {
  const domContext = await extractDOMContext();

  return {
    url: domContext.url,
    title: domContext.title,
    timestamp: domContext.timestamp,
    language: domContext.language,
    viewport: domContext.viewport,
    content: domContext.content,
    contentTruncated: domContext.contentTruncated,
    dom: {
      buttons: domContext.buttons,
      links: domContext.links,
      forms: domContext.forms,
      metadata: domContext.metadata,
    },
  };
}

export default {
  registerVisualTools,
  extractPageContext
};
